import { useCallback, useEffect, useRef, useState, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react';
import { BLEND_MODE_GROUPS, BLEND_MODE_LABELS } from '../../core/blendModes';
import { hexToRgba, rgbaToCss, rgbaToHex } from '../../core/color';
import { BUILTIN_GRADIENTS, gradientToCss } from '../../core/gradient';
import { findLayerById, isGroup, isSurfaceLayer } from '../../core/layerTree';
import {
  defaultBevel,
  defaultColorOverlay,
  defaultDropShadow,
  defaultGradientOverlay,
  defaultInnerGlow,
  defaultInnerShadow,
  defaultOuterGlow,
  defaultPatternOverlay,
  defaultStroke,
  hasActiveEffects,
  readLayerEffects,
  shadowOffset,
  type BevelEffect,
  type BevelStyle,
  type ColorOverlayEffect,
  type DropShadowEffect,
  type GradientOverlayEffect,
  type InnerGlowEffect,
  type InnerShadowEffect,
  type LayerEffects,
  type OuterGlowEffect,
  type PatternOverlayEffect,
  type StrokeEffect,
  type StrokePosition,
} from '../../core/layerStyles';
import { allPatterns } from '../../core/patterns';
import { cloneBlendIf } from '../../core/blendIf';
import type { BlendChannels, BlendIf, BlendMode, DocumentMeta, Layer, RGBA } from '../../core/types';
import { DEFAULT_BLEND_CHANNELS, DEFAULT_BLEND_IF } from '../../core/types';
import { patchLayer, patchLayerTransient, setLayerEffects } from '../../state/actions';
import { Checkbox, Modal, NumberInput, Select, SliderRow } from '../components/controls';
import { BlendIfControls } from './BlendIfControls';

type EffectKey = keyof LayerEffects;
type Selection = 'blending' | EffectKey;

const BLEND_OPTIONS = BLEND_MODE_GROUPS.flat().map((mode) => ({
  value: mode,
  label: BLEND_MODE_LABELS[mode],
}));

const EFFECT_CATALOG: {
  key: EffectKey;
  label: string;
  defaults: () => NonNullable<LayerEffects[EffectKey]>;
}[] = [
  { key: 'bevel', label: 'Bevel & Emboss', defaults: defaultBevel },
  { key: 'stroke', label: 'Stroke', defaults: defaultStroke },
  { key: 'innerShadow', label: 'Inner Shadow', defaults: defaultInnerShadow },
  { key: 'innerGlow', label: 'Inner Glow', defaults: defaultInnerGlow },
  { key: 'colorOverlay', label: 'Color Overlay', defaults: defaultColorOverlay },
  { key: 'gradientOverlay', label: 'Gradient Overlay', defaults: defaultGradientOverlay },
  {
    key: 'patternOverlay',
    label: 'Pattern Overlay',
    defaults: () => defaultPatternOverlay(allPatterns()[0]?.id ?? ''),
  },
  { key: 'outerGlow', label: 'Outer Glow', defaults: defaultOuterGlow },
  { key: 'dropShadow', label: 'Drop Shadow', defaults: defaultDropShadow },
];

const STROKE_POSITIONS: { value: StrokePosition; label: string }[] = [
  { value: 'outside', label: 'Outside' },
  { value: 'inside', label: 'Inside' },
  { value: 'center', label: 'Center' },
];

const BEVEL_STYLES: { value: BevelStyle; label: string }[] = [
  { value: 'outerBevel', label: 'Outer Bevel' },
  { value: 'innerBevel', label: 'Inner Bevel' },
  { value: 'emboss', label: 'Emboss' },
  { value: 'pillowEmboss', label: 'Pillow Emboss' },
];


interface LayerStyleSnapshot {
  effects: LayerEffects | null;
  opacity: number;
  blendMode: BlendMode;
  blendChannels: BlendChannels;
  blendIf: BlendIf;
}

const CHANNEL_KEYS: { key: keyof BlendChannels; label: string }[] = [
  { key: 'r', label: 'R' },
  { key: 'g', label: 'G' },
  { key: 'b', label: 'B' },
  { key: 'a', label: 'A' },
];

function cloneEffects(effects: LayerEffects | null | undefined): LayerEffects | null {
  if (!effects) return null;
  return structuredClone(effects);
}

/** Only effects that will paint are sent to the engine. */
function activeEffectsOnly(draft: LayerEffects): LayerEffects | null {
  const out: LayerEffects = {};
  for (const { key } of EFFECT_CATALOG) {
    const fx = draft[key];
    if (fx?.enabled) Object.assign(out, { [key]: fx });
  }
  return hasActiveEffects(out) ? out : null;
}

function globalLightAngle(draft: LayerEffects): number {
  return draft.dropShadow?.angle ?? draft.innerShadow?.angle ?? draft.bevel?.angle ?? 120;
}

function SectionHeader({ title }: { title: string }) {
  return <h4 className="layer-style__section-header">{title}</h4>;
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="layer-style__section">
      <SectionHeader title={title} />
      <div className="layer-style__pane">{children}</div>
    </div>
  );
}

function ColorSwatch({
  label,
  color,
  onChange,
}: {
  label: string;
  color: RGBA;
  onChange: (color: RGBA) => void;
}) {
  return (
    <div className="field">
      <span className="field__label">{label}</span>
      <label className="layer-style__color-swatch" style={{ background: rgbaToCss(color) }}>
        <input
          type="color"
          value={rgbaToHex(color)}
          onChange={(event) => onChange(hexToRgba(event.target.value, color.a))}
        />
      </label>
    </div>
  );
}

function BlendModeField({
  value,
  onChange,
}: {
  value: BlendMode;
  onChange: (mode: BlendMode) => void;
}) {
  return (
    <div className="field">
      <span className="field__label">Blend Mode</span>
      <Select value={value} options={BLEND_OPTIONS} onChange={onChange} />
    </div>
  );
}

function OpacityField({
  opacity,
  onChange,
  label = 'Opacity',
}: {
  opacity: number;
  onChange: (opacity: number) => void;
  label?: string;
}) {
  return (
    <SliderRow
      label={label}
      value={Math.round(opacity * 100)}
      min={0}
      max={100}
      onChange={(value) => onChange(value / 100)}
    />
  );
}

function AngleControl({
  angle,
  onChange,
  useGlobalLight,
  onGlobalLightChange,
}: {
  angle: number;
  onChange: (angle: number) => void;
  useGlobalLight: boolean;
  onGlobalLightChange: (value: boolean) => void;
}) {
  const dialRef = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);

  const angleFromEvent = useCallback((clientX: number, clientY: number) => {
    const el = dialRef.current;
    if (!el) return angle;
    const rect = el.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const dx = clientX - cx;
    const dy = clientY - cy;
    const deg = (Math.atan2(-dy, dx) * 180) / Math.PI;
    return Math.round(deg);
  }, [angle]);

  const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    dragging.current = true;
    event.currentTarget.setPointerCapture(event.pointerId);
    onChange(angleFromEvent(event.clientX, event.clientY));
  };

  const onPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!dragging.current) return;
    onChange(angleFromEvent(event.clientX, event.clientY));
  };

  const onPointerUp = (event: ReactPointerEvent<HTMLDivElement>) => {
    dragging.current = false;
    event.currentTarget.releasePointerCapture(event.pointerId);
  };

  return (
    <>
      <div className="layer-style__angle-row">
        <span className="field__label">Angle</span>
        <div
          ref={dialRef}
          className="layer-style__angle-dial"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          aria-label="Angle dial"
        >
          <span className="layer-style__angle-hand" style={{ transform: `rotate(${angle - 90}deg)` }} />
        </div>
        <NumberInput value={angle} min={-180} max={180} onChange={onChange} />
      </div>
      <label className="checkbox layer-style__global-light">
        <input
          type="checkbox"
          checked={useGlobalLight}
          onChange={(event) => onGlobalLightChange(event.target.checked)}
        />
        Use Global Light
      </label>
    </>
  );
}

function StylePreviewSwatch({ draft }: { draft: LayerEffects }) {
  const dropShadow = draft.dropShadow;
  const stroke = draft.stroke;
  const colorOverlay = draft.colorOverlay;

  let innerStyle: React.CSSProperties = { background: '#4a90d9' };

  if (colorOverlay?.enabled) {
    innerStyle = {
      background: rgbaToCss({
        ...colorOverlay.color,
        a: colorOverlay.color.a * colorOverlay.opacity,
      }),
    };
  }

  if (stroke?.enabled) {
    innerStyle.border = `${Math.min(stroke.size, 6)}px solid ${rgbaToCss({
      ...stroke.color,
      a: stroke.color.a * stroke.opacity,
    })}`;
  }

  if (dropShadow?.enabled) {
    const offset = shadowOffset(dropShadow.angle, Math.min(dropShadow.distance, 12));
    const alpha = dropShadow.color.a * dropShadow.opacity;
    const { r, g, b } = dropShadow.color;
    innerStyle.boxShadow = `${offset.x.toFixed(1)}px ${offset.y.toFixed(1)}px ${Math.min(dropShadow.size, 16)}px rgba(${Math.round(r * 255)}, ${Math.round(g * 255)}, ${Math.round(b * 255)}, ${alpha.toFixed(2)})`;
  }

  return (
    <div className="layer-style__swatch-wrap">
      <span className="layer-style__swatch-label">Preview</span>
      <div className="layer-style__swatch">
        <div className="layer-style__swatch-inner" style={innerStyle} />
      </div>
    </div>
  );
}

function BlendingPane({
  blendMode,
  opacity,
  blendChannels,
  blendIf,
  onBlendModeChange,
  onOpacityChange,
  onBlendChannelsChange,
  onBlendIfChange,
}: {
  blendMode: BlendMode;
  opacity: number;
  blendChannels: BlendChannels;
  blendIf: BlendIf;
  onBlendModeChange: (mode: BlendMode) => void;
  onOpacityChange: (opacity: number) => void;
  onBlendChannelsChange: (channels: BlendChannels) => void;
  onBlendIfChange: (blendIf: BlendIf) => void;
}) {
  return (
    <>
      <h3 className="layer-style__pane-title">Blending Options</h3>
      <Section title="General">
        <BlendModeField value={blendMode} onChange={onBlendModeChange} />
        <OpacityField opacity={opacity} onChange={onOpacityChange} />
      </Section>
      <Section title="Advanced">
        <div className="field">
          <span className="field__label">Channels</span>
          <div className="layer-style__channels">
            {CHANNEL_KEYS.map(({ key, label }) => (
              <Checkbox
                key={key}
                label={label}
                checked={blendChannels[key]}
                onChange={(checked) =>
                  onBlendChannelsChange({ ...blendChannels, [key]: checked })
                }
              />
            ))}
          </div>
        </div>
        <BlendIfControls value={blendIf} onChange={onBlendIfChange} />
      </Section>
    </>
  );
}

function EffectControls({
  effectKey,
  draft,
  onChange,
  useGlobalLight,
  onGlobalLightChange,
  onAngleChange,
}: {
  effectKey: EffectKey;
  draft: LayerEffects;
  onChange: (next: LayerEffects) => void;
  useGlobalLight: boolean;
  onGlobalLightChange: (value: boolean) => void;
  onAngleChange: (angle: number) => void;
}) {
  const fx = draft[effectKey];
  if (!fx) {
    return <p className="dialog__note">Select this effect to edit its settings.</p>;
  }

  const patch = <T extends NonNullable<LayerEffects[EffectKey]>>(next: T) =>
    onChange({ ...draft, [effectKey]: next });

  const currentAngle =
    effectKey === 'dropShadow' || effectKey === 'innerShadow' || effectKey === 'bevel'
      ? (fx as DropShadowEffect | InnerShadowEffect | BevelEffect).angle
      : globalLightAngle(draft);

  switch (effectKey) {
    case 'dropShadow': {
      const e = fx as DropShadowEffect;
      return (
        <>
          <h3 className="layer-style__pane-title">Drop Shadow</h3>
          <Section title="Structure">
            <BlendModeField value={e.blendMode} onChange={(blendMode) => patch({ ...e, blendMode })} />
            <OpacityField opacity={e.opacity} onChange={(opacity) => patch({ ...e, opacity })} />
            <ColorSwatch label="Color" color={e.color} onChange={(color) => patch({ ...e, color })} />
            <AngleControl
              angle={currentAngle}
              onChange={onAngleChange}
              useGlobalLight={useGlobalLight}
              onGlobalLightChange={onGlobalLightChange}
            />
            <SliderRow label="Distance" value={e.distance} min={0} max={250} onChange={(distance) => patch({ ...e, distance })} />
            <SliderRow label="Spread" value={e.spread} min={0} max={100} onChange={(spread) => patch({ ...e, spread })} />
            <SliderRow label="Size" value={e.size} min={0} max={250} onChange={(size) => patch({ ...e, size })} />
          </Section>
        </>
      );
    }
    case 'innerShadow': {
      const e = fx as InnerShadowEffect;
      return (
        <>
          <h3 className="layer-style__pane-title">Inner Shadow</h3>
          <Section title="Structure">
            <BlendModeField value={e.blendMode} onChange={(blendMode) => patch({ ...e, blendMode })} />
            <OpacityField opacity={e.opacity} onChange={(opacity) => patch({ ...e, opacity })} />
            <ColorSwatch label="Color" color={e.color} onChange={(color) => patch({ ...e, color })} />
            <AngleControl
              angle={currentAngle}
              onChange={onAngleChange}
              useGlobalLight={useGlobalLight}
              onGlobalLightChange={onGlobalLightChange}
            />
            <SliderRow label="Distance" value={e.distance} min={0} max={250} onChange={(distance) => patch({ ...e, distance })} />
            <SliderRow label="Choke" value={e.choke} min={0} max={100} onChange={(choke) => patch({ ...e, choke })} />
            <SliderRow label="Size" value={e.size} min={0} max={250} onChange={(size) => patch({ ...e, size })} />
          </Section>
        </>
      );
    }
    case 'outerGlow': {
      const e = fx as OuterGlowEffect;
      return (
        <>
          <h3 className="layer-style__pane-title">Outer Glow</h3>
          <Section title="Elements">
            <BlendModeField value={e.blendMode} onChange={(blendMode) => patch({ ...e, blendMode })} />
            <OpacityField opacity={e.opacity} onChange={(opacity) => patch({ ...e, opacity })} />
            <ColorSwatch label="Color" color={e.color} onChange={(color) => patch({ ...e, color })} />
            <SliderRow label="Spread" value={e.spread} min={0} max={100} onChange={(spread) => patch({ ...e, spread })} />
            <SliderRow label="Size" value={e.size} min={0} max={250} onChange={(size) => patch({ ...e, size })} />
          </Section>
        </>
      );
    }
    case 'innerGlow': {
      const e = fx as InnerGlowEffect;
      return (
        <>
          <h3 className="layer-style__pane-title">Inner Glow</h3>
          <Section title="Elements">
            <BlendModeField value={e.blendMode} onChange={(blendMode) => patch({ ...e, blendMode })} />
            <OpacityField opacity={e.opacity} onChange={(opacity) => patch({ ...e, opacity })} />
            <ColorSwatch label="Color" color={e.color} onChange={(color) => patch({ ...e, color })} />
            <SliderRow label="Choke" value={e.choke} min={0} max={100} onChange={(choke) => patch({ ...e, choke })} />
            <SliderRow label="Size" value={e.size} min={0} max={250} onChange={(size) => patch({ ...e, size })} />
          </Section>
        </>
      );
    }
    case 'bevel': {
      const e = fx as BevelEffect;
      return (
        <>
          <h3 className="layer-style__pane-title">Bevel &amp; Emboss</h3>
          <Section title="Structure">
            <OpacityField opacity={e.opacity} onChange={(opacity) => patch({ ...e, opacity })} />
            <div className="field">
              <span className="field__label">Style</span>
              <Select value={e.style} options={BEVEL_STYLES} onChange={(style) => patch({ ...e, style })} />
            </div>
            <SliderRow label="Depth" value={e.depth} min={0} max={1000} onChange={(depth) => patch({ ...e, depth })} />
            <SliderRow label="Size" value={e.size} min={0} max={250} onChange={(size) => patch({ ...e, size })} />
            <SliderRow label="Soften" value={e.soften} min={0} max={16} onChange={(soften) => patch({ ...e, soften })} />
          </Section>
          <Section title="Shading">
            <AngleControl
              angle={currentAngle}
              onChange={onAngleChange}
              useGlobalLight={useGlobalLight}
              onGlobalLightChange={onGlobalLightChange}
            />
            <SliderRow label="Altitude" value={e.altitude} min={0} max={90} onChange={(altitude) => patch({ ...e, altitude })} />
            <ColorSwatch label="Highlight" color={e.highlightColor} onChange={(highlightColor) => patch({ ...e, highlightColor })} />
            <div className="field">
              <span className="field__label">Highlight Mode</span>
              <Select value={e.highlightMode} options={BLEND_OPTIONS} onChange={(highlightMode) => patch({ ...e, highlightMode })} />
            </div>
            <SliderRow
              label="Highlight Opacity"
              value={Math.round(e.highlightOpacity * 100)}
              min={0}
              max={100}
              onChange={(value) => patch({ ...e, highlightOpacity: value / 100 })}
            />
            <ColorSwatch label="Shadow" color={e.shadowColor} onChange={(shadowColor) => patch({ ...e, shadowColor })} />
            <div className="field">
              <span className="field__label">Shadow Mode</span>
              <Select value={e.shadowMode} options={BLEND_OPTIONS} onChange={(shadowMode) => patch({ ...e, shadowMode })} />
            </div>
            <SliderRow
              label="Shadow Opacity"
              value={Math.round(e.shadowOpacity * 100)}
              min={0}
              max={100}
              onChange={(value) => patch({ ...e, shadowOpacity: value / 100 })}
            />
          </Section>
        </>
      );
    }
    case 'stroke': {
      const e = fx as StrokeEffect;
      return (
        <>
          <h3 className="layer-style__pane-title">Stroke</h3>
          <Section title="Structure">
            <BlendModeField value={e.blendMode} onChange={(blendMode) => patch({ ...e, blendMode })} />
            <OpacityField opacity={e.opacity} onChange={(opacity) => patch({ ...e, opacity })} />
            <ColorSwatch label="Color" color={e.color} onChange={(color) => patch({ ...e, color })} />
            <SliderRow label="Size" value={e.size} min={1} max={250} onChange={(size) => patch({ ...e, size })} />
            <div className="field">
              <span className="field__label">Position</span>
              <Select value={e.position} options={STROKE_POSITIONS} onChange={(position) => patch({ ...e, position })} />
            </div>
          </Section>
        </>
      );
    }
    case 'colorOverlay': {
      const e = fx as ColorOverlayEffect;
      return (
        <>
          <h3 className="layer-style__pane-title">Color Overlay</h3>
          <Section title="Structure">
            <BlendModeField value={e.blendMode} onChange={(blendMode) => patch({ ...e, blendMode })} />
            <OpacityField opacity={e.opacity} onChange={(opacity) => patch({ ...e, opacity })} />
            <ColorSwatch label="Color" color={e.color} onChange={(color) => patch({ ...e, color })} />
          </Section>
        </>
      );
    }
    case 'gradientOverlay': {
      const e = fx as GradientOverlayEffect;
      const gradientOptions = BUILTIN_GRADIENTS.map((gradient) => ({
        value: gradient.id,
        label: gradient.name,
      }));
      return (
        <>
          <h3 className="layer-style__pane-title">Gradient Overlay</h3>
          <Section title="Structure">
            <BlendModeField value={e.blendMode} onChange={(blendMode) => patch({ ...e, blendMode })} />
            <OpacityField opacity={e.opacity} onChange={(opacity) => patch({ ...e, opacity })} />
          </Section>
          <Section title="Elements">
            <div className="field field--stack">
              <span className="field__label">Gradient</span>
              <Select
                value={e.gradient.id}
                options={gradientOptions}
                onChange={(id) => {
                  const gradient = BUILTIN_GRADIENTS.find((item) => item.id === id);
                  if (gradient) patch({ ...e, gradient });
                }}
              />
              <div
                className="layer-style__gradient-strip"
                style={{ background: `linear-gradient(90deg, ${gradientToCss(e.gradient)})` }}
              />
            </div>
            <SliderRow label="Angle" value={e.angle} min={-180} max={180} onChange={(angle) => patch({ ...e, angle })} />
            <SliderRow label="Scale" value={e.scale} min={10} max={150} onChange={(scale) => patch({ ...e, scale })} />
            <Checkbox label="Reverse" checked={e.reverse} onChange={(reverse) => patch({ ...e, reverse })} />
          </Section>
        </>
      );
    }
    case 'patternOverlay': {
      const e = fx as PatternOverlayEffect;
      const patternOptions = allPatterns().map((pattern) => ({
        value: pattern.id,
        label: pattern.name,
      }));
      return (
        <>
          <h3 className="layer-style__pane-title">Pattern Overlay</h3>
          <Section title="Structure">
            <BlendModeField value={e.blendMode} onChange={(blendMode) => patch({ ...e, blendMode })} />
            <OpacityField opacity={e.opacity} onChange={(opacity) => patch({ ...e, opacity })} />
          </Section>
          <Section title="Elements">
            {patternOptions.length > 0 ? (
              <div className="field">
                <span className="field__label">Pattern</span>
                <Select
                  value={e.patternId || patternOptions[0].value}
                  options={patternOptions}
                  onChange={(patternId) => patch({ ...e, patternId })}
                />
              </div>
            ) : (
              <p className="dialog__note">No patterns loaded. Define one from the Edit menu.</p>
            )}
            <SliderRow label="Scale" value={e.scale} min={10} max={200} onChange={(scale) => patch({ ...e, scale })} />
          </Section>
        </>
      );
    }
    default:
      return null;
  }
}

function LayerStyleEditor({
  layerId,
  initialEffects,
  initialOpacity,
  initialBlendMode,
  initialBlendChannels,
  initialBlendIf,
  allowEffects,
  onClose,
}: {
  layerId: string;
  initialEffects: LayerEffects | null | undefined;
  initialOpacity: number;
  initialBlendMode: BlendMode;
  initialBlendChannels: BlendChannels;
  initialBlendIf: BlendIf;
  allowEffects: boolean;
  onClose: () => void;
}) {
  const appliedRef = useRef(false);
  const snapshotRef = useRef<LayerStyleSnapshot>({
    effects: cloneEffects(initialEffects),
    opacity: initialOpacity,
    blendMode: initialBlendMode,
    blendChannels: { ...initialBlendChannels },
    blendIf: cloneBlendIf(initialBlendIf),
  });

  const [draft, setDraft] = useState<LayerEffects>(() => readLayerEffects(initialEffects) ?? {});
  const [blendDraft, setBlendDraft] = useState<{
    opacity: number;
    blendMode: BlendMode;
    blendChannels: BlendChannels;
    blendIf: BlendIf;
  }>(() => ({
    opacity: initialOpacity,
    blendMode: initialBlendMode,
    blendChannels: { ...initialBlendChannels },
    blendIf: cloneBlendIf(initialBlendIf),
  }));
  const [selected, setSelected] = useState<Selection>('blending');
  const [previewEnabled, setPreviewEnabled] = useState(true);
  const [useGlobalLight, setUseGlobalLight] = useState(true);

  useEffect(() => {
    if (!allowEffects) return;
    const effects = previewEnabled ? activeEffectsOnly(draft) : snapshotRef.current.effects;
    setLayerEffects(layerId, effects, false);
  }, [layerId, draft, previewEnabled, allowEffects]);

  useEffect(() => {
    patchLayerTransient(layerId, {
      opacity: blendDraft.opacity,
      blendMode: blendDraft.blendMode,
      blendChannels: blendDraft.blendChannels,
      blendIf: blendDraft.blendIf,
    });
  }, [layerId, blendDraft]);

  useEffect(() => {
    const snapshot = snapshotRef.current;
    return () => {
      if (appliedRef.current) return;
      patchLayerTransient(layerId, {
        ...(allowEffects ? { effects: snapshot.effects } : {}),
        opacity: snapshot.opacity,
        blendMode: snapshot.blendMode,
        blendChannels: snapshot.blendChannels,
        blendIf: snapshot.blendIf,
      });
    };
  }, [layerId, allowEffects]);

  const cancel = () => {
    onClose();
  };

  const apply = () => {
    appliedRef.current = true;
    patchLayer(
      layerId,
      {
        ...(allowEffects ? { effects: activeEffectsOnly(draft) } : {}),
        opacity: blendDraft.opacity,
        blendMode: blendDraft.blendMode,
        blendChannels: blendDraft.blendChannels,
        blendIf: blendDraft.blendIf,
      },
      'Layer Style',
    );
    onClose();
  };

  const selectItem = (id: Selection) => {
    setSelected(id);
    if (id === 'blending') return;
    setDraft((current) => {
      if (current[id]) return current;
      const catalog = EFFECT_CATALOG.find((item) => item.key === id)!;
      return { ...current, [id]: { ...catalog.defaults(), enabled: false } };
    });
  };

  const toggleEffect = (key: EffectKey, enabled: boolean) => {
    setDraft((current) => {
      const catalog = EFFECT_CATALOG.find((item) => item.key === key)!;
      const existing = current[key] ?? { ...catalog.defaults(), enabled: false };
      return { ...current, [key]: { ...existing, enabled } };
    });
    setSelected(key);
  };

  const setAngle = (angle: number) => {
    setDraft((current) => {
      if (!useGlobalLight) {
        if (selected !== 'dropShadow' && selected !== 'innerShadow' && selected !== 'bevel') return current;
        const fx = current[selected];
        if (!fx) return current;
        return { ...current, [selected]: { ...fx, angle } };
      }
      const next: LayerEffects = { ...current };
      if (next.dropShadow) next.dropShadow = { ...next.dropShadow, angle };
      if (next.innerShadow) next.innerShadow = { ...next.innerShadow, angle };
      if (next.bevel) next.bevel = { ...next.bevel, angle };
      return next;
    });
  };

  const paneTitle =
    selected === 'blending'
      ? 'Blending Options'
      : EFFECT_CATALOG.find((item) => item.key === selected)?.label ?? 'Layer Style';

  return (
    <div className="layer-style">
      <nav className="layer-style__sidebar" aria-label="Layer styles">
        <ul className="layer-style__list">
          <li
            className={`layer-style__item layer-style__item--blending${selected === 'blending' ? ' layer-style__item--active' : ''}`}
          >
            <span className="layer-style__checkbox-spacer" aria-hidden="true" />
            <button type="button" className="layer-style__label" onClick={() => selectItem('blending')}>
              Blending Options
            </button>
          </li>
          {allowEffects &&
            EFFECT_CATALOG.map(({ key, label }) => {
              const enabled = Boolean(draft[key]?.enabled);
              return (
                <li
                  key={key}
                  className={`layer-style__item${selected === key ? ' layer-style__item--active' : ''}`}
                >
                  <input
                    type="checkbox"
                    checked={enabled}
                    onChange={(event) => toggleEffect(key, event.target.checked)}
                    aria-label={`Enable ${label}`}
                  />
                  <button type="button" className="layer-style__label" onClick={() => selectItem(key)}>
                    {label}
                  </button>
                </li>
              );
            })}
        </ul>
      </nav>

      <div className="layer-style__center" aria-label={paneTitle}>
        {selected === 'blending' ? (
          <BlendingPane
            blendMode={blendDraft.blendMode}
            opacity={blendDraft.opacity}
            blendChannels={blendDraft.blendChannels}
            blendIf={blendDraft.blendIf}
            onBlendModeChange={(blendMode) => setBlendDraft((current) => ({ ...current, blendMode }))}
            onOpacityChange={(opacity) => setBlendDraft((current) => ({ ...current, opacity }))}
            onBlendChannelsChange={(blendChannels) =>
              setBlendDraft((current) => ({ ...current, blendChannels }))
            }
            onBlendIfChange={(blendIf) => setBlendDraft((current) => ({ ...current, blendIf }))}
          />
        ) : (
          <EffectControls
            effectKey={selected}
            draft={draft}
            onChange={setDraft}
            useGlobalLight={useGlobalLight}
            onGlobalLightChange={setUseGlobalLight}
            onAngleChange={setAngle}
          />
        )}
      </div>

      <aside className="layer-style__actions">
        <button type="button" className="button button--primary" onClick={apply}>
          OK
        </button>
        <button type="button" className="button" onClick={cancel}>
          Cancel
        </button>
        <label className="checkbox layer-style__preview-check">
          <input
            type="checkbox"
            checked={previewEnabled}
            onChange={(event) => setPreviewEnabled(event.target.checked)}
          />
          Preview
        </label>
        <StylePreviewSwatch draft={draft} />
      </aside>
    </div>
  );
}

export function LayerStyleDialog({
  doc,
  open,
  onClose,
}: {
  doc: DocumentMeta;
  open: boolean;
  onClose: () => void;
}) {
  const activeLayer = doc.activeLayerId ? findLayerById(doc.layers, doc.activeLayerId) : null;

  if (!activeLayer) {
    return (
      <Modal open={open} title="Layer Style" floating onClose={onClose}>
        <p className="dialog__note">Select a layer to edit blending options.</p>
      </Modal>
    );
  }

  return (
    <LayerStyleDialogBody
      open={open}
      layer={activeLayer}
      onClose={onClose}
    />
  );
}

function LayerStyleDialogBody({
  open,
  layer,
  onClose,
}: {
  open: boolean;
  layer: Layer;
  onClose: () => void;
}) {
  // Groups share Blending Options and layer styles; fill/adjustment keep blending only.
  const allowEffects = isSurfaceLayer(layer) || isGroup(layer);
  return (
    <Modal
      open={open}
      title="Layer Style"
      className="dialog--layer-style"
      floating
      onClose={onClose}
    >
      {open ? (
        <LayerStyleEditor
          key={layer.id}
          layerId={layer.id}
          initialEffects={layer.effects}
          initialOpacity={layer.opacity}
          initialBlendMode={layer.blendMode}
          initialBlendChannels={layer.blendChannels ?? DEFAULT_BLEND_CHANNELS}
          initialBlendIf={layer.blendIf ?? DEFAULT_BLEND_IF}
          allowEffects={allowEffects}
          onClose={onClose}
        />
      ) : null}
    </Modal>
  );
}
